// Nomad — The Rolex Edit landing page
// Requires lenis.min.js, gsap.min.js, ScrollTrigger.min.js loaded before this file.

// --- Preloader: intentionally independent of GSAP/Lenis, so it still
// works (and clears itself) even if either of those fails to load. ---
(function () {
  const preloader = document.querySelector('[data-rolex-preloader]');
  if (!preloader) return;

  const fill = preloader.querySelector('[data-preloader-fill]');
  document.documentElement.style.overflow = 'hidden';

  function updateProgress(pct) {
    if (fill) fill.style.width = Math.min(100, Math.round(pct)) + '%';
  }

  function finish() {
    updateProgress(100);
    setTimeout(() => {
      preloader.classList.add('is-hidden');
      document.documentElement.style.overflow = '';
      setTimeout(() => preloader.remove(), 700);
    }, 200);
  }

  // If the Hero section has a frame sequence configured, treat those
  // images as the real thing worth waiting for. Otherwise just wait
  // for the window load event with a smooth-looking fake progress bar.
  const heroFramesScript = document.querySelector('[data-hero-frames]');
  let frameUrls = [];
  try {
    frameUrls = heroFramesScript ? JSON.parse(heroFramesScript.textContent) : [];
  } catch (e) {
    frameUrls = [];
  }

  if (frameUrls.length) {
    let loaded = 0;
    const total = frameUrls.length;

    frameUrls.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loaded += 1;
        updateProgress((loaded / total) * 100);
        if (loaded >= total) finish();
      };
      img.src = url;
    });
  } else {
    let fakeProgress = 0;
    const fakeInterval = setInterval(() => {
      fakeProgress = Math.min(90, fakeProgress + Math.random() * 15);
      updateProgress(fakeProgress);
    }, 150);

    const onWindowLoad = () => {
      clearInterval(fakeInterval);
      finish();
    };

    if (document.readyState === 'complete') {
      onWindowLoad();
    } else {
      window.addEventListener('load', onWindowLoad);
    }
  }

  // Safety net: never let the preloader block the page for more than 6s,
  // regardless of what's still loading.
  setTimeout(finish, 6000);
})();

(function () {
  if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
    console.warn('[rolex-edit] GSAP or Lenis not loaded — skipping scroll init');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // --- Lenis smooth scroll, driven by GSAP's ticker (not its own rAF) ---
  const lenis = new Lenis({ autoRaf: false });
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0); // prevents Lenis/GSAP ticker fighting each other

  lenis.on('scroll', ScrollTrigger.update);

  // --- Stay in sync with the theme's cart/search drawer scroll lock ---
  // bodyScrollLock.min.js locks scroll by mutating <body> (inline style
  // and/or a class) when a drawer opens. Lenis has no visibility into
  // that on its own, so it'll keep smooth-scrolling underneath a
  // supposedly-locked drawer unless we tell it to stop. This watches
  // for that mutation rather than hooking a specific drawer event,
  // since the exact open/close trigger isn't known from this file alone.
  const isBodyScrollLocked = () =>
    document.body.style.overflow === 'hidden' ||
    document.body.classList.contains('overflow-hidden') ||
    document.body.style.position === 'fixed';

  let lenisIsStopped = false;
  const syncLenisToBodyLock = () => {
    const locked = isBodyScrollLocked();
    if (locked && !lenisIsStopped) {
      lenis.stop();
      lenisIsStopped = true;
    } else if (!locked && lenisIsStopped) {
      lenis.start();
      lenisIsStopped = false;
    }
  };

  new MutationObserver(syncLenisToBodyLock).observe(document.body, {
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  // --- 01 Hero: scroll-scrubbed canvas frame sequence ---
  const heroCanvas = document.querySelector('[data-hero-canvas]');
  const heroFramesScript = document.querySelector('[data-hero-frames]');

  if (heroCanvas && heroFramesScript) {
    let frameUrls = [];
    try {
      frameUrls = JSON.parse(heroFramesScript.textContent);
    } catch (e) {
      console.warn('[rolex-edit] Could not parse hero frame URLs', e);
    }

    if (frameUrls.length) {
      const ctx = heroCanvas.getContext('2d');
      const images = frameUrls.map((url) => {
        const img = new Image();
        img.src = url;
        return img;
      });

      const frameState = { frame: 0 };

      const resizeCanvas = () => {
        const rect = heroCanvas.getBoundingClientRect();
        heroCanvas.width = rect.width * window.devicePixelRatio;
        heroCanvas.height = rect.height * window.devicePixelRatio;
      };

      const drawFrame = () => {
        const img = images[Math.round(frameState.frame)];
        if (!img || !img.complete || !img.naturalWidth) return;

        ctx.clearRect(0, 0, heroCanvas.width, heroCanvas.height);

        const canvasRatio = heroCanvas.width / heroCanvas.height;
        const imgRatio = img.naturalWidth / img.naturalHeight;
        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgRatio > canvasRatio) {
          drawHeight = heroCanvas.height;
          drawWidth = img.naturalWidth * (heroCanvas.height / img.naturalHeight);
          offsetX = (heroCanvas.width - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = heroCanvas.width;
          drawHeight = img.naturalHeight * (heroCanvas.width / img.naturalWidth);
          offsetX = 0;
          offsetY = (heroCanvas.height - drawHeight) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      };

      images[0].onload = () => {
        resizeCanvas();
        drawFrame();

        window.addEventListener('resize', () => {
          resizeCanvas();
          drawFrame();
        });

        gsap.to(frameState, {
          frame: frameUrls.length - 1,
          ease: 'none',
          scrollTrigger: {
            trigger: heroCanvas.closest('section'),
            start: 'top top',
            end: '+=800',
            scrub: true,
          },
          onUpdate: drawFrame,
        });
      };

      // First frame failed to load (not uploaded yet, wrong filename, etc.)
      // — hide the canvas so the static fallback image behind it shows.
      images[0].onerror = () => {
        console.warn('[rolex-edit] Hero frame sequence failed to load — falling back to static image.');
        heroCanvas.style.display = 'none';
      };
    } else {
      // No frames configured at all — hide canvas immediately, fallback shows.
      heroCanvas.style.display = 'none';
    }
  }

  // --- Generic scrub-parallax (Day I, Day II, Craft, Detail images) ---
  document.querySelectorAll('[data-parallax]').forEach((el) => {
    const speed = parseFloat(el.dataset.parallax) || 0.15;
    gsap.to(el, {
      yPercent: speed * 100,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
  });

  // --- Generic fade-up on enter (text blocks, CTAs, intros) ---
  document.querySelectorAll('[data-fade-up]').forEach((el) => {
    gsap.from(el, {
      y: 40,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // --- 06 Ethos Quote: bg settles from a slight scale, words stagger in ---
  const quote = document.querySelector('[data-rolex-quote]');
  if (quote) {
    const bg = quote.querySelector('[data-quote-bg]');
    const words = quote.querySelectorAll('[data-quote-line]');

    if (bg) {
      gsap.fromTo(
        bg,
        { scale: 1.08 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: quote,
            start: 'top bottom',
            end: 'top top',
            scrub: true,
          },
        }
      );
    }

    if (words.length) {
      gsap.from(words, {
        opacity: 0,
        y: 20,
        stagger: 0.06,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: quote,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });
    }
  }

  // --- 07 Models We Do: horizontal scroll-jack (desktop only — mobile
  // gets a natural swipeable row via CSS overflow-x + scroll-snap,
  // since pinned scroll-jacking is notoriously unreliable on touch) ---
  const modelsSection = document.querySelector('[data-rolex-models]');
  if (modelsSection && window.innerWidth > 768) {
    const track = modelsSection.querySelector('[data-models-track]');

    if (track) {
      const getScrollDistance = () => Math.max(0, track.scrollWidth - modelsSection.offsetWidth);

      gsap.to(track, {
        x: () => -getScrollDistance(),
        ease: 'none',
        scrollTrigger: {
          trigger: modelsSection,
          start: 'top top',
          end: () => `+=${getScrollDistance()}`,
          scrub: true,
          pin: true,
          invalidateOnRefresh: true,
        },
      });
    }
  }

  // --- 08 Custom Strap Process: click-to-swap tab toggle, no scroll tie-in ---
  const process = document.querySelector('[data-rolex-process]');
  if (process) {
    const steps = process.querySelectorAll('[data-process-step]');
    const toggles = process.querySelectorAll('[data-process-toggle]');

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const target = toggle.dataset.processToggle;

        toggles.forEach((t) => t.classList.toggle('is-active', t === toggle));
        steps.forEach((s) => s.classList.toggle('is-active', s.dataset.processStep === target));
      });
    });
  }

  // --- Recalculate trigger positions once every image has actually loaded ---
  window.addEventListener('load', () => ScrollTrigger.refresh());
})();
